/**
 * MRB eBook Classroom — no-login student access for sections/mrb-ebook-classroom.liquid.
 *
 * A teacher's classroom link carries a membership-term access token in the
 * URL FRAGMENT (never sent to any server):
 *   /pages/ebook-classroom#class=v1.<cid>.<exp>.<sig>.<lambdaSub>
 *
 * This loader parses it, probes the presigner once to sort valid / expired /
 * invalid, then hands the exact same {endpoint, cid, exp, sig} contract to
 * the library app (dynamic import via the versioned importmap alias) — the
 * full grid, filters, and flip reader work with zero sign-in. Reading
 * progress stays per-device (localStorage under the teacher's customer id),
 * which is what a shared classroom device wants.
 *
 * No link? Students can also TYPE the class code (DANNY-4287) into the ask
 * state's form — the presigner exchanges ?code= for the same token parts
 * (server-side normalization is forgiving about case/spaces/dashes), the URL
 * gains the equivalent #class= fragment via replaceState so reloads and
 * bookmarks keep working, and the same mount path runs.
 *
 * Any successful entry (link or code) also REMEMBERS the classroom on the
 * device (localStorage): later visits to the bare URL probe the saved token
 * and auto-open the library — type-it-once, like Epic. A saved token that
 * comes back expired/invalid (membership ended, secret rotated) is forgotten
 * and the code form simply returns; a network blip keeps the memory.
 */

import { EBOOK_MANIFEST } from '@mrb/ebook-manifest';

const LAMBDA_HOST_SUFFIX = '.lambda-url.us-east-2.on.aws/';
const LINK_RE = /class=v1\.(\d+)\.(\d+)\.([0-9a-f]{64})\.([a-z0-9]+)/i;
const STORE_KEY = 'mrb:classroom:v1';

const page = document.querySelector('[data-cls-page]');

function remember(sub, cid, exp, sig) {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify({ sub, cid, exp, sig }));
  } catch (e) { /* storage blocked — remembering is best-effort */ }
}

function recall() {
  try {
    const saved = JSON.parse(localStorage.getItem(STORE_KEY));
    if (saved && saved.sub && saved.cid && saved.exp && saved.sig) return saved;
  } catch (e) { /* corrupt/blocked — treat as no memory */ }
  return null;
}

function forget() {
  try {
    localStorage.removeItem(STORE_KEY);
  } catch (e) { /* ignore */ }
}

/** One presigner round-trip sorts a token: 200 valid, 401 expired, 403
 *  invalid/rotated, 0 network failure. */
async function probeStatus(endpoint, cid, exp, sig) {
  try {
    const url = new URL(endpoint);
    url.searchParams.set('h', EBOOK_MANIFEST[0].handle);
    url.searchParams.set('cid', cid);
    url.searchParams.set('exp', exp);
    url.searchParams.set('sig', sig);
    const res = await fetch(url, { cache: 'no-store' });
    return res.status;
  } catch (e) {
    return 0;
  }
}

function show(which, message) {
  for (const name of ['ask', 'error', 'app']) {
    const el = page.querySelector(`[data-cls-${name}]`);
    if (el) el.hidden = name !== which;
  }
  if (message) {
    const msgEl = page.querySelector('[data-cls-error-msg]');
    if (msgEl) msgEl.textContent = message;
  }
}

async function mount(endpoint, cid, exp, sig) {
  const root = page.querySelector('[data-ebl-root]');
  root.dataset.endpoint = endpoint;
  root.dataset.cid = cid;
  root.dataset.exp = exp;
  root.dataset.sig = sig;
  show('app');
  await import('@mrb/ebook-library'); // self-initializes on [data-ebl-root]
}

/** Class-code entry: exchange the typed code at the presigner for token
 *  parts, then run the same mount path as a link. The presigner host comes
 *  from data-cls-sub (server-rendered from the endpoint metafield). */
function wireCodeForm() {
  const form = page.querySelector('[data-cls-form]');
  if (!form) return;
  const sub = page.dataset.clsSub;
  if (!sub) {
    form.hidden = true;
    return;
  }
  const input = form.querySelector('[data-cls-input]');
  const go = form.querySelector('[data-cls-go]');
  const err = page.querySelector('[data-cls-form-err]');

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    const raw = input.value.trim();
    err.hidden = true;
    if (!raw) {
      input.focus();
      return;
    }
    go.disabled = true;
    go.textContent = 'Checking…';
    let status = 0;
    let body = null;
    try {
      const res = await fetch(
        `https://${sub}${LAMBDA_HOST_SUFFIX}?code=${encodeURIComponent(raw)}`,
        { cache: 'no-store' },
      );
      status = res.status;
      body = await res.json().catch(() => null);
    } catch (e) {
      status = -1;
    }
    go.disabled = false;
    go.textContent = 'Start Reading';

    if (status === 200 && body && body.cid) {
      // Persist the equivalent link so reload/bookmark keeps working, and
      // remember the classroom so this device auto-opens next visit.
      window.history.replaceState(null, '',
        `#class=v1.${body.cid}.${body.exp}.${body.sig}.${sub}`);
      remember(sub, body.cid, body.exp, body.sig);
      await mount(`https://${sub}${LAMBDA_HOST_SUFFIX}`, body.cid, body.exp, body.sig);
      return;
    }
    err.textContent =
      status === 404 ? 'Hmm, that code didn’t work. Check each letter and number, then try again.'
      : status === 401 ? 'That code has expired — ask your teacher for a new one.'
      : status === 429 ? 'Too many tries! Wait a few minutes, then check the code with your teacher.'
      : 'We couldn’t connect right now. Check the internet, then try again.';
    err.hidden = false;
    input.select();
  });
}

async function boot() {
  if (!page) return;

  const match = window.location.hash.match(LINK_RE);
  if (match) {
    // A link in the URL is authoritative (a fresh link must beat any memory).
    const [, cid, exp, sig, sub] = match;
    const endpoint = `https://${sub}${LAMBDA_HOST_SUFFIX}`;
    const status = await probeStatus(endpoint, cid, exp, sig);
    if (status === 0) {
      show('error', 'We couldn’t reach the library right now. Check the internet connection and reload this page.');
      return;
    }
    if (status === 401) {
      show('error', 'This class link has expired — ask your teacher for a fresh one.');
      return;
    }
    if (status !== 200) {
      show('error', 'This class link isn’t quite right — ask your teacher to copy it again from their library page.');
      return;
    }
    remember(sub, cid, exp, sig);
    await mount(endpoint, cid, exp, sig);
    return;
  }

  // No link — a device that has opened this classroom before auto-resumes.
  const saved = recall();
  if (saved && Number(saved.exp) * 1000 > Date.now()) {
    const endpoint = `https://${saved.sub}${LAMBDA_HOST_SUFFIX}`;
    const status = await probeStatus(endpoint, saved.cid, saved.exp, saved.sig);
    if (status === 200) {
      window.history.replaceState(null, '',
        `#class=v1.${saved.cid}.${saved.exp}.${saved.sig}.${saved.sub}`);
      await mount(endpoint, saved.cid, saved.exp, saved.sig);
      return;
    }
    if (status !== 0) forget(); // expired or rotated; a network blip keeps it
  } else if (saved) {
    forget(); // memory already past its expiry
  }

  show('ask');
  wireCodeForm();
}

boot();
